import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import { API_BASE_URL } from '../config/api';
import axios from 'axios';

const SharedPerformanceCacheContext = createContext();

export const useSharedPerformanceCache = () => {
  const context = useContext(SharedPerformanceCacheContext);
  if (!context) {
    throw new Error('useSharedPerformanceCache must be used within a SharedPerformanceCacheProvider');
  }
  return context;
};

/**
 * Shared Performance Cache Provider
 * 
 * Single source of truth for all optimized device performance data.
 * Prevents reloading when switching tabs - data loads once and is cached
 * until organization or date range changes.
 */
export const SharedPerformanceCacheProvider = ({ children }) => {
  const cacheRef = useRef({
    cacheKey: null,
    devices: [],
    data: {},
    loadingStates: {},
    errors: {},
    dateRange: null
  });
  
  const [cacheInfo, setCacheInfo] = useState({
    isLoading: false,
    devicesLoaded: 0,
    totalDevices: 0,
    endpointsLoaded: 0
  });
  
  const loadingRef = useRef(false);
  const pendingPromiseRef = useRef(null);

  // Generate cache key
  const generateCacheKey = useCallback((orgId, startDate, endDate) => {
    return `${orgId}-${startDate}-${endDate}`;
  }, []);

  // Check if we need to reload data
  const needsReload = useCallback((orgId, startDate, endDate) => {
    const currentKey = generateCacheKey(orgId, startDate, endDate);
    return cacheRef.current.cacheKey !== currentKey;
  }, [generateCacheKey]);

  // Load all performance data for organization
  const loadAllPerformanceData = useCallback(async (orgId, startDate, endDate) => {
    const cacheKey = generateCacheKey(orgId, startDate, endDate);
    
    // Return cached data if available - exit immediately
    if (!needsReload(orgId, startDate, endDate)) {
      console.log('📊 Using FULL CACHED performance data - no API calls needed');
      setCacheInfo(prev => ({
        ...prev,
        isLoading: false,
        devicesLoaded: cacheRef.current.devices.length,
        totalDevices: cacheRef.current.devices.length,
        endpointsLoaded: Object.keys(cacheRef.current.data).length
      }));
      return cacheRef.current.data;
    }

    // Prevent duplicate loading
    if (loadingRef.current) {
      console.log('📊 Already loading performance data, waiting for current load to finish...');
      try {
        await pendingPromiseRef.current;
      } catch (error) {
        // ignore - will trigger reload below
      }

      if (!needsReload(orgId, startDate, endDate)) {
        console.log('📊 After wait, cache now has requested data');
        return cacheRef.current.data;
      }
    }

    console.log('📊 Loading FRESH performance data for all tables...');
    loadingRef.current = true;
    
    // Reset cache for new parameters
    cacheRef.current = {
      cacheKey,
      devices: [],
      data: {},
      loadingStates: {},
      errors: {},
      dateRange: { startDate, endDate }
    };
    
    setCacheInfo({
      isLoading: true,
      devicesLoaded: 0,
      totalDevices: 0,
      endpointsLoaded: 0
    });

    try {
      const loadPromise = (async () => {
        const token = localStorage.getItem('authToken');
      
      // Step 1: Load devices
      const orgResponse = await axios.get(`${API_BASE_URL}/organizations/${orgId}/devices`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const devices = orgResponse.data.devices || [];
      cacheRef.current.devices = devices;
      
      setCacheInfo(prev => ({
        ...prev,
        totalDevices: devices.length
      }));

      console.log(`📊 Found ${devices.length} devices, loading ALL endpoint data...`);

      // Step 2: Load all endpoint data for each device
      const requestConfig = {
        headers: { 'Authorization': `Bearer ${token}` },
        params: { startDate, endDate }
      };

      const endpoints = [
        'connection-count',
        'payload-count', 
        'connectivity-pattern',
        'battery-daily',
        'temperature-daily',
        'rssi-daily',
        'boot-reason',
        'success-rate-daily'
      ];

      // Load organization endpoints ONCE per endpoint (not per device)
      const allPromises = [];
      
      // Initialize loading states for all device-endpoint combinations
      devices.forEach(device => {
        endpoints.forEach(endpoint => {
          const dataKey = `${endpoint}-${device.id}`;
          cacheRef.current.loadingStates[dataKey] = true;
        });
      });
      
      console.log('🚀 OPTIMIZED: Making', endpoints.length, 'organization calls instead of', devices.length * endpoints.length, 'device calls');
      
      endpoints.forEach(endpoint => {
        const promise = new Promise(async (resolve) => {
          try {
            console.log(`🚀 ORGANIZATION: Loading ${endpoint} data for ${devices.length} devices...`);
            
            const response = await axios.get(`${API_BASE_URL}/organizations/${orgId}/performance/${endpoint}`, requestConfig);
            
            // Distribute organization response data to all devices
            devices.forEach(device => {
              const dataKey = `${endpoint}-${device.id}`;
              
              // Find this device's data in the organization response
              let deviceData = null;
              if (response.data.devices) {
                deviceData = response.data.devices.find(d => d.deviceId === device.id);
              }
              
              if (deviceData) {
                cacheRef.current.data[dataKey] = deviceData;
              } else {
                // No data for this device - use empty structure matching device endpoint format
                cacheRef.current.data[dataKey] = {
                  deviceId: device.id,
                  dateRange: { startDate, endDate },
                  summary: { totalTransmissions: 0, totalPayloads: 0 },
                  dailyConnectivity: [],
                  dailyPayloads: []
                };
              }
              
              cacheRef.current.loadingStates[dataKey] = false;
            });
            
            setCacheInfo(prev => ({
              ...prev,
              endpointsLoaded: prev.endpointsLoaded + devices.length
            }));
            
            console.log(`✅ ORGANIZATION: Loaded ${endpoint} data for ${response.data.devices?.length || 0} devices`);
            resolve({ endpoint, success: true });
          } catch (error) {
            // Mark all devices as failed for this endpoint
            devices.forEach(device => {
              const dataKey = `${endpoint}-${device.id}`;
              cacheRef.current.errors[dataKey] = error.message;
              cacheRef.current.loadingStates[dataKey] = false;
            });
            
            console.error(`❌ ORGANIZATION: Failed ${endpoint}:`, error.message);
            resolve({ endpoint, success: false, error: error.message });
          }
        });
        
        allPromises.push(promise);
      });

        await Promise.all(allPromises);
      
      setCacheInfo(prev => ({
        ...prev,
        isLoading: false,
        devicesLoaded: devices.length
      }));
      
        console.log('✅ ALL performance data loaded and cached');
        return cacheRef.current.data;
      })();

      pendingPromiseRef.current = loadPromise;
      const result = await loadPromise;
      pendingPromiseRef.current = null;
      loadingRef.current = false;
      return result;

    } catch (error) {
      console.error('❌ Error loading performance data:', error);
      setCacheInfo(prev => ({ ...prev, isLoading: false }));
      pendingPromiseRef.current = null;
      loadingRef.current = false;
      throw error;
    }
  }, [generateCacheKey, needsReload]);

  // Get specific endpoint data for device
  const getEndpointData = useCallback((endpoint, deviceId) => {
    const dataKey = `${endpoint}-${deviceId}`;
    return cacheRef.current.data[dataKey] || null;
  }, []);

  // Get device list
  const getDevices = useCallback(() => {
    return cacheRef.current.devices;
  }, []);

  // Check if specific endpoint is loading
  const isEndpointLoading = useCallback((endpoint, deviceId) => {
    const dataKey = `${endpoint}-${deviceId}`;
    return cacheRef.current.loadingStates[dataKey] || false;
  }, []);

  // Calculate summary statistics from device data
  const getSummaryStats = useCallback(() => {
    const devices = cacheRef.current.devices;
    console.log(`🔍 getSummaryStats called with ${devices.length} devices`);
    if (!devices.length) return null;

    let totalDevices = devices.length;
    let activeDevices = devices.filter(d => d.isactivated).length;
    let devicesWithData = 0;
    let totalTransmissions = 0;
    let rssiValues = [];
    let batteryVoltages = [];
    let successRates = [];
    let connectionPercentages = [];

    const { dateRange } = cacheRef.current;
    const { startDate, endDate } = dateRange || {};
    const MS_PER_DAY = 24 * 60 * 60 * 1000;

    let periodDays = null;
    if (startDate && endDate) {
      const startUtc = new Date(`${startDate}T00:00:00Z`);
      const endUtc = new Date(`${endDate}T00:00:00Z`);
      if (!isNaN(startUtc.getTime()) && !isNaN(endUtc.getTime())) {
        periodDays = Math.max(Math.floor((endUtc - startUtc) / MS_PER_DAY) + 1, 0);
      }
    }

    let totalConnectedDays = 0;
    let totalPossibleDeviceDays = 0;

    console.log(`🔍 Starting connection calculation for ${totalDevices} devices`);
    
    devices.forEach(device => {
      let hasAnyData = false;
      
      // Connectivity data
      const connectivityData = getEndpointData('connection-count', device.id);
      if (connectivityData?.summary?.totalTransmissions) {
        totalTransmissions += connectivityData.summary.totalTransmissions;
        hasAnyData = true;
      }

      let deviceTotalDays = periodDays !== null ? periodDays : 0;
      let deviceConnectedDays = 0;

      const dailyConnectivity = connectivityData?.dailyConnectivity;
      if (Array.isArray(dailyConnectivity) && dailyConnectivity.length > 0) {
        const observedTotalDays = dailyConnectivity.length;
        deviceConnectedDays = dailyConnectivity.filter(day => day.connected === true).length;
        if (periodDays === null) {
          deviceTotalDays = observedTotalDays;
        }

        const denominator = deviceTotalDays > 0 ? deviceTotalDays : observedTotalDays;
        if (denominator > 0) {
          const percentage = Math.round(((Math.min(deviceConnectedDays, denominator) / denominator) * 100) * 10) / 10;
          connectionPercentages.push(percentage);
        }

        hasAnyData = true;
      }

      if (periodDays !== null && deviceTotalDays === 0) {
        deviceTotalDays = periodDays;
      }

      totalConnectedDays += Math.min(deviceConnectedDays, deviceTotalDays);
      totalPossibleDeviceDays += deviceTotalDays;
      
      // Battery data
      const batteryData = getEndpointData('battery-daily', device.id);
      if (batteryData?.summary?.overallAverage && batteryData.summary.overallAverage > 0) {
        batteryVoltages.push(batteryData.summary.overallAverage);
        hasAnyData = true;
      }
      
      // RSSI data
      const rssiData = getEndpointData('rssi-daily', device.id);
      if (rssiData?.summary?.overallAverage && rssiData.summary.overallAverage !== 0) {
        rssiValues.push(Math.round(Math.abs(rssiData.summary.overallAverage)));
        hasAnyData = true;
      }
      
      // Success rate data (admin only) - matching SimpleSuccessRateTable structure
      const successData = getEndpointData('success-rate-daily', device.id);
      if (successData?.summary?.combinedSuccessRate !== null && successData?.summary?.combinedSuccessRate !== undefined) {
        successRates.push(successData.summary.combinedSuccessRate);
        hasAnyData = true;
      } else if (successData?.dailySuccessRate && Array.isArray(successData.dailySuccessRate)) {
        // Calculate from daily success rates
        const validRates = successData.dailySuccessRate
          .map(day => day.combinedSuccessRate)
          .filter(rate => rate !== null && rate !== undefined && !isNaN(rate));
        if (validRates.length > 0) {
          const avgRate = validRates.reduce((sum, rate) => sum + rate, 0) / validRates.length;
          successRates.push(Math.round(avgRate));
          hasAnyData = true;
        }
      }
      
      if (hasAnyData) devicesWithData++;
    });
    
    // Calculate battery health percentage
    let batteryHealthPercentage = null;
    if (batteryVoltages.length > 0) {
      const avgVoltage = batteryVoltages.reduce((sum, v) => sum + v, 0) / batteryVoltages.length;
      const minVoltage = 2.4;
      const maxVoltage = 3.05;
      if (avgVoltage <= minVoltage) {
        batteryHealthPercentage = 0;
      } else if (avgVoltage >= maxVoltage) {
        batteryHealthPercentage = 100;
      } else {
        batteryHealthPercentage = ((avgVoltage - minVoltage) / (maxVoltage - minVoltage)) * 100;
      }
      batteryHealthPercentage = Math.round(batteryHealthPercentage * 10) / 10;
    }
    
    // Calculate overall connection percentage: sensors with connections / total sensors
    const overallConnectionPercentage = totalPossibleDeviceDays > 0
      ? Math.round(((totalConnectedDays / totalPossibleDeviceDays) * 100) * 10) / 10
      : null;
    
    console.log(`📊 Connection days calculation: ${totalConnectedDays}/${totalPossibleDeviceDays} = ${overallConnectionPercentage}%`);

    return {
      totalDevices,
      activeDevices,
      devicesWithData,
      totalTransmissions,
      averageRSSI: rssiValues.length > 0 ? Math.round(rssiValues.reduce((sum, v) => sum + v, 0) / rssiValues.length) : null,
      averageSuccessRate: successRates.length > 0 ? Math.round(successRates.reduce((sum, v) => sum + v, 0) / successRates.length) : null,
      averageConnectionPercentage: overallConnectionPercentage,
      batteryHealthPercentage,
      averageBatteryVoltage: batteryVoltages.length > 0 ? batteryVoltages.reduce((sum, v) => sum + v, 0) / batteryVoltages.length : null,
      // Distribution data for histograms
      connectionPercentages,
      rssiValues,
      batteryVoltages,
      successRates
    };
  }, [getEndpointData]);
  
  // Check if summary data is ready (all relevant endpoints loaded)
  const isSummaryReady = useCallback((includeSuccessRate = false) => {
    const devices = cacheRef.current.devices;
    if (!devices.length) return false;
    
    const requiredEndpoints = ['connection-count', 'battery-daily', 'rssi-daily'];
    if (includeSuccessRate) requiredEndpoints.push('success-rate-daily');
    
    return devices.every(device => 
      requiredEndpoints.every(endpoint => 
        !isEndpointLoading(endpoint, device.id)
      )
    );
  }, [isEndpointLoading]);

  const contextValue = {
    loadAllPerformanceData,
    getEndpointData,
    getDevices,
    isEndpointLoading,
    getSummaryStats,
    isSummaryReady,
    cacheInfo,
    needsReload
  };

  return (
    <SharedPerformanceCacheContext.Provider value={contextValue}>
      {children}
    </SharedPerformanceCacheContext.Provider>
  );
};
